import api from 'api';
import { OrganizationStatus } from 'lib/enums';
import { OrganizationTypeValue } from 'lib/enums/OrganizationType';
import { EOrganization, ERequestTypes } from 'lib/types';
import { getFirebaseContext } from 'utils/firebase';
import { createStripeCustomer } from 'api/stripe';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';

export type RegisterOrganizationAddress = {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: number | undefined;
  zipCode: string;
  phone: string;
  email: string;
};

export const EMPTY_ADDRESS: RegisterOrganizationAddress = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: undefined,
  zipCode: '',
  phone: '',
  email: ''
};

type CreateOrganizationParams = {
  organizationType: OrganizationTypeValue;
  name: string;
  address: string | undefined;
  addressLine2: string | undefined;
  city: string | undefined;
  state: number | undefined;
  zipCode: string | undefined;
  phone: string | undefined;
  organizationEmail: string | undefined;
  createdBy: string | undefined;
  stripeId?: string;
  userName: string;
  email: string;
};

export const createOrganization = async ({
  organizationType,
  name,
  address,
  addressLine2,
  city,
  state,
  zipCode,
  phone,
  organizationEmail,
  createdBy,
  stripeId,
  userName,
  email
}: CreateOrganizationParams) => {
  const organizationData: Partial<EOrganization> = {
    organizationType,
    organizationStatus: OrganizationStatus.not_applicable.value,
    name,
    address,
    addressLine2: addressLine2 || '',
    city,
    state,
    zipCode,
    phone,
    email: organizationEmail,
    createdBy,
    showInSearch: true
  };

  // Create stripe customer id for organization
  if (stripeId) {
    organizationData.stripeId = stripeId;
  } else {
    const [error, response] = await createStripeCustomer(
      userName,
      email,
      createdBy ?? ''
    );
    if (error) {
      logAndCaptureException(
        ColumnService.AUTH_AND_USER_MANAGEMENT,
        error,
        'Unable to create Stripe customer',
        {
          userName,
          email,
          createdBy
        }
      );
    }

    if (response) {
      organizationData.stripeId = response;
    }
  }

  // TODO: better typing for different types of organizations
  // Had to as any here because this org is missing
  // fields like fullPageTemplate which is not relevant for a govt
  const newlyCreatedOrganization = await getFirebaseContext()
    .organizationsRef()
    .add(organizationData as EOrganization);

  return newlyCreatedOrganization;
};

export type RegistrationOrganizationType =
  ERequestTypes['users/register-organization']['organizationType'];

export async function registerOrganizationAsUser({
  organizationType,
  address
}: {
  organizationType: RegistrationOrganizationType;
  address: RegisterOrganizationAddress;
}) {
  const { state } = address;
  if (state === undefined) {
    throw new Error('Cannot register an organization without a state');
  }

  const result = await api.safePost('users/register-organization', {
    organizationType,
    ...address,
    state
  });
  if (result.error) {
    throw new Error(`Unable to register organization: ${result.error}`);
  }
}
